import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { ChevronLeft, Search, X, AlertTriangle, CheckCircle2, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Gate } from "@/components/wallet/Gate";
import { SignSheet } from "@/components/wallet/SignSheet";
import { useWallet } from "@/lib/wallet/store";
import { quoteFlatFee } from "@/lib/wallet/fees";
import { overviewQuery } from "@/lib/wallet/queries";
import { addressFingerprint, formatAmount, truncateAddress } from "@/lib/wallet/format";
import {
  isValidAddress,
  planTrustlineRemoval,
  trustlineStillExists,
  type TokenBalance,
} from "@/lib/wallet/xrpl";
import { CURATED_XRPL_ASSETS, type CuratedAsset } from "@/lib/wallet/assets";
import { searchTokens, getRiskLabel, type CatalogToken } from "@/lib/wallet/catalog";

export const Route = createFileRoute("/trustlines")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Trustlines — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Add, limit or remove XRP Ledger trustlines so you only hold the issued tokens you choose.",
      },
      { property: "og:title", content: "Trustlines — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Explicit trustline control: you decide which issuers you accept.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <TrustlineScreen />
    </Gate>
  ),
});

function encodeCurrency(code: string): string {
  const upper = code.trim();
  if (/^[A-Za-z0-9?!@#$%^&*<>(){}[\]|]{3}$/.test(upper)) return upper.toUpperCase();
  const hex = Array.from(new TextEncoder().encode(upper))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
  return hex.padEnd(40, "0").slice(0, 40);
}

function TrustlineScreen() {
  const { address, submit } = useWallet();
  const overview = useQuery(overviewQuery(address));
  const [code, setCode] = useState("");
  const [issuer, setIssuer] = useState("");
  const [limit, setLimit] = useState("");
  const [valid, setValid] = useState<boolean | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<TokenBalance | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");


  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 180);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const search = useQuery({
    queryKey: ["token-catalog", debouncedSearch.toLowerCase()],
    queryFn: () => searchTokens(debouncedSearch),
    enabled: debouncedSearch.length >= 1,
    placeholderData: (prev) => prev,
    staleTime: 60_000,
    retry: 1,
  });

  const searching = searchQuery.trim().length >= 1;
  const results = searching ? search.data ?? [] : [];

  const reserveCost = overview.data?.ownerReserve ?? 0.2;

  const chooseAsset = (asset: CuratedAsset | CatalogToken) => {
    if ("symbol" in asset && !("defaultLimit" in asset)) {
      // CatalogToken
      setCode(asset.symbol);
      setIssuer(asset.issuer);
      setLimit("1000000000"); // Default for new ones
    } else {
      // CuratedAsset
      const ca = asset as CuratedAsset;
      setCode(ca.symbol);
      setIssuer(ca.issuer);
      setLimit(ca.defaultLimit);
    }
    setValid(true);
    setSearchQuery("");
  };

  const save = async () => {
    setBusy(true);
    try {
      const result = await submit({
        TransactionType: "TrustSet",
        Account: address ?? "",
        LimitAmount: {
          currency: encodeCurrency(code),
          issuer: issuer.trim(),
          value: limit.trim() || "0",
        },
        Flags: 0x00020000, // tfSetNoRipple — don't act as a rippling hop
      }, { fee: quoteFlatFee("Trustline") });
      if (result.succeeded) {
        toast.success("Trustline updated.");
        setSheetOpen(false);
        setCode("");
        setIssuer("");
        setLimit("");
        setValid(null);
        void overview.refetch();
      } else {
        toast.error(`Ledger rejected: ${result.result}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Trustline update failed.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Deletes a trustline. The ledger only removes the line — and only then
   * releases the locked owner reserve — when the line is left in its *default*
   * state with a zero balance. "Default" depends on the account's DefaultRipple
   * setting, so the right no-ripple flag is computed from live ledger state
   * rather than assumed, and the deletion is verified afterwards.
   */
  const removeLine = async (token: TokenBalance) => {
    setBusy(true);
    try {
      const plan = await planTrustlineRemoval(address ?? "", token.currency, token.issuer);

      if (!plan.exists) {
        toast.success(`${token.code} trustline is already removed.`);
        setRemoveTarget(null);
        void overview.refetch();
        return;
      }
      if (Number(plan.balance) !== 0) {
        toast.error(
          `Send or swap the remaining ${formatAmount(plan.balance, true)} ${token.code} away first — the ledger keeps a line with a balance.`,
        );
        return;
      }

      const result = await submit({
        TransactionType: "TrustSet",
        Account: address ?? "",
        LimitAmount: { currency: token.currency, issuer: token.issuer, value: "0" },
        Flags: plan.flags,
      });

      if (!result.succeeded) {
        toast.error(`Ledger rejected: ${result.result}`);
        return;
      }

      const stillThere = await trustlineStillExists(address ?? "", token.currency, token.issuer);
      if (stillThere) {
        toast.error(
          `${token.code} limit set to zero, but the issuer still holds the line open, so the reserve stays locked.`,
        );
      } else {
        toast.success(
          `${token.code} trustline deleted · ${formatAmount(reserveCost, true)} XRP reserve released`,
        );
      }
      setRemoveTarget(null);
      void overview.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Trustline removal failed.");
    } finally {
      setBusy(false);
    }
  };


  const canReview = Boolean(valid && code.trim() && limit.trim() !== "");


  return (
    <div className="animate-rise space-y-4">
      <div className="flex items-center gap-2">
        <Link
          to="/"
          className="grid size-8 place-items-center rounded-lg border border-border text-muted"
        >
          <ChevronLeft className="size-4" />
        </Link>
        <h1 className="text-[18px] font-medium tracking-tight">Trustlines</h1>
      </div>

      <section className="panel space-y-4 p-4">
        <div className="space-y-2">
          <p className="label-instrument">Recommended assets</p>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
            <input
              type="search"
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or symbol..."
              className="readout h-11 w-full rounded-lg border border-border bg-background pl-9 pr-9 text-[13px] outline-none placeholder:text-faint focus:border-accent"
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-full text-faint hover:bg-subtle hover:text-ink"
                aria-label="Clear search"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>

          {searching && results.length === 0 && search.isFetching ? (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-border bg-subtle/30 p-3">
              <Loader2 className="size-3.5 animate-spin text-accent" />
              <span className="text-[11px] text-faint">Searching XRPL tokens…</span>
            </div>
          ) : results.length > 0 ? (
            <div className="mt-2 space-y-2 rounded-lg border border-border bg-subtle/30 p-2">
              <div className="flex items-center justify-between px-1">
                <p className="text-[10px] font-medium uppercase tracking-wider text-faint">
                  {results.length} result{results.length === 1 ? "" : "s"}
                </p>
                {search.isFetching ? <Loader2 className="size-3 animate-spin text-accent" /> : null}
              </div>
              <div className="grid max-h-72 grid-cols-1 gap-1 overflow-y-auto">
                {results.map((token) => {
                  const risk = getRiskLabel(token.trustLevel);
                  return (
                    <button
                      key={`${token.issuer}-${token.currency}`}
                      type="button"
                      onClick={() => chooseAsset(token)}
                      className="flex items-center gap-3 rounded-md p-2 text-left hover:bg-subtle"
                    >
                      {token.icon ? (
                        <img src={token.icon} alt="" loading="lazy" className="size-6 rounded-full bg-background" />
                      ) : (
                        <div className="grid size-6 place-items-center rounded-full bg-border text-[10px] font-bold">
                          {token.symbol[0]}
                        </div>
                      )}
                      <div className="flex-1 overflow-hidden">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[13px] font-semibold">{token.symbol}</span>
                          {risk.isRisk ? (
                            <AlertTriangle className="size-3 text-danger" />
                          ) : token.trustLevel >= 3 ? (
                            <CheckCircle2 className="size-3 text-success" />
                          ) : null}
                          <span className={`text-[9px] font-medium ${risk.color}`}>
                            {risk.label}
                          </span>
                        </div>
                        <span className="block truncate text-[10px] text-faint">{token.name}</span>
                      </div>
                      <span className="text-[10px] text-faint">{truncateAddress(token.issuer, 4, 4)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : searching && !search.isFetching ? (
            <div className="rounded-lg border border-dashed border-border p-3 text-center">
              <p className="text-[11px] text-faint">No XRPL tokens matched “{searchQuery.trim()}”.</p>
            </div>
          ) : null}


          {!searchQuery && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {CURATED_XRPL_ASSETS.map((asset) => {
                const selected = issuer === asset.issuer && code === asset.symbol;
                return (
                  <button
                    key={asset.issuer}
                    type="button"
                    onClick={() => chooseAsset(asset)}
                    className={`interactive-lift rounded-lg border p-3 text-left ${
                      selected ? "chip-active" : "border-border bg-background"
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <img
                        src={asset.logo}
                        alt={`${asset.name} logo`}
                        className="size-8 shrink-0 rounded-full bg-elevated object-cover"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold">{asset.symbol}</span>
                        <span className="mt-0.5 block truncate text-[10px] text-muted">{asset.name}</span>
                      </span>
                      <span className="readout text-[8px] uppercase text-faint">
                        {asset.category === "stablecoin" ? "verified" : asset.category}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="h-px bg-border/50" />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="label-instrument">Currency code</p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              spellCheck={false}
              placeholder="USD or RLUSD"
              className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[13px] outline-none placeholder:text-faint focus:border-accent"
            />
          </div>
          <div>
            <p className="label-instrument">Limit</p>
            <input
              value={limit}
              onChange={(e) => setLimit(e.target.value.replace(/[^0-9.]/g, ""))}
              inputMode="decimal"
              placeholder="0 removes it"
              className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[13px] outline-none placeholder:text-faint focus:border-accent"
            />
          </div>
        </div>
        <div>
          <p className="label-instrument">Issuer address</p>
          <input
            value={issuer}
            onChange={(e) => {
              setIssuer(e.target.value);
              setValid(null);
            }}
            onBlur={async () => setValid(await isValidAddress(issuer.trim()))}
            spellCheck={false}
            placeholder="r…"
            className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[12px] outline-none placeholder:text-faint focus:border-accent"
          />
          {valid === false ? (
            <p className="readout mt-1.5 text-[10px] text-danger flex items-center gap-1">
              <AlertTriangle className="size-3" /> Not a valid issuer address.
            </p>
          ) : valid ? (
            <p className="readout mt-1.5 text-[10px] text-success flex items-center gap-1">
              <CheckCircle2 className="size-3" /> Fingerprint {addressFingerprint(issuer.trim())}
            </p>
          ) : (
            <p className="readout mt-1.5 text-[10px] leading-relaxed text-faint flex items-start gap-1.5">
              <Info className="mt-0.5 size-3 shrink-0" />
              <span>
                Only add issuers you have verified yourself. A trustline to a hostile issuer can send
                you worthless tokens.
              </span>
            </p>
          )}
        </div>
        
        {issuer && !CURATED_XRPL_ASSETS.find(a => a.issuer === issuer) && (
          <div className="rounded-lg bg-danger/5 border border-danger/20 p-3">
             <div className="flex items-center gap-2 text-danger">
                <AlertTriangle className="size-4" />
                <p className="text-[11px] font-semibold uppercase tracking-wider">Risk Warning</p>
             </div>
             <p className="mt-1 text-[10px] leading-relaxed text-danger/80">
                This asset is not in our curated list. Many XRPL tokens are "memecoins" with no value,
                utility, or backing. Always verify the issuer domain and reputation before proceeding.
             </p>
          </div>
        )}

        <p className="readout text-[10px] text-faint">
          Each trustline locks {formatAmount(reserveCost, true)} XRP of owner reserve until removed.
        </p>
        <button
          type="button"
          disabled={!canReview}
          onClick={() => setSheetOpen(true)}
          className="readout h-11 w-full rounded-lg bg-accent text-[11px] uppercase tracking-[0.15em] text-accent-ink disabled:opacity-40"
        >
          Review trustline
        </button>
      </section>

      <section>
        <p className="readout mb-2.5 text-[11px] uppercase tracking-[0.15em] text-muted">Existing</p>
        <div className="panel divide-y divide-border">
          {(overview.data?.tokens ?? []).length === 0 ? (
            <p className="readout p-4 text-[11px] text-faint">No trustlines on this account.</p>
          ) : (
            overview.data!.tokens.map((t) => (
              <div key={`${t.currency}-${t.issuer}`} className="px-3.5 py-3">
                <div className="flex items-center justify-between">
                  <p className="text-[13px] font-medium">{t.code}</p>
                  <p className="readout text-[12px]">{formatAmount(t.balance, true)}</p>
                </div>
                <div className="readout mt-1 flex items-center justify-between text-[10px] text-faint">
                  <span>{truncateAddress(t.issuer, 6, 6)}</span>
                  <span>
                    limit {formatAmount(t.limit)} {t.noRipple ? "· no-ripple" : ""}
                  </span>
                </div>
                {Number(t.balance) > 0 ? (
                  <p className="readout mt-2 text-[10px] leading-relaxed text-faint">
                    Send or swap the remaining {formatAmount(t.balance, true)} {t.code} away before
                    this line can be deleted.
                  </p>
                ) : null}
                <div className="mt-2 flex items-center gap-3">
                  <button
                    type="button"
                    disabled={Number(t.balance) > 0 || t.frozen}
                    onClick={() => setRemoveTarget(t)}
                    className="readout text-[10px] uppercase tracking-[0.15em] text-danger disabled:opacity-40"
                  >
                    Delete line · refund {formatAmount(reserveCost, true)} XRP
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCode(t.code);
                      setIssuer(t.issuer);
                      setLimit("0");
                      setValid(true);
                    }}
                    className="readout ml-auto text-[10px] uppercase tracking-[0.15em] text-accent"
                  >
                    Edit limit
                  </button>
                </div>

              </div>
            ))
          )}
        </div>
      </section>

      <SignSheet
        open={sheetOpen}
        title="Sign trustline"
        checksum={valid ? "✓" : "!"}
        busy={busy}
        rows={[
          { label: "Operation", value: "TrustSet · no-ripple" },
          { label: "Currency", value: code.trim().toUpperCase() },
          { label: "Issuer", value: issuer.trim() },
          { label: "Fingerprint", value: addressFingerprint(issuer.trim()), emphasis: "accent" },
          {
            label: "Limit",
            value: limit.trim() === "0" ? "0 — removes the line" : formatAmount(limit),
          },
        ]}
        warning="A trustline authorizes this issuer to place balances in your account. Verify the issuer independently."
        onCancel={() => setSheetOpen(false)}
        onConfirm={() => void save()}
      />

      <SignSheet
        open={Boolean(removeTarget)}
        title="Delete trustline"
        checksum={removeTarget ? addressFingerprint(removeTarget.issuer) : "!"}
        busy={busy}
        rows={[
          { label: "Operation", value: "TrustSet · limit 0" },
          { label: "Currency", value: removeTarget?.code ?? "" },
          { label: "Issuer", value: removeTarget?.issuer ?? "" },
          { label: "Balance", value: formatAmount(removeTarget?.balance ?? 0, true) },
          {
            label: "Reserve refunded",
            value: `+${formatAmount(reserveCost, true)} XRP`,
            emphasis: "accent",
          },
        ]}
        warning="Deleting the line removes this issuer's authorization and releases the locked owner reserve back to your spendable XRP. Only the network fee is spent — no service fee."
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (removeTarget) void removeLine(removeTarget);
        }}
      />

    </div>
  );
}
